import moment from "jalali-moment";
import { ReactElement, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import Switch from "react-ios-switch";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import { useResponsive } from "../../hooks/useResponsive";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import { Sidebar } from "../../layouts/Sidebar";

const JsonModal = ({
  title,
  data,
  onClose,
}: {
  title: string;
  data: any;
  onClose: () => void;
}) => {
  const renderValue = (val: any, depth = 0): ReactElement => {
    if (val === null) return <span style={{ color: "#ef4444" }}>null</span>;
    if (typeof val === "boolean")
      return <span style={{ color: "#8b5cf6" }}>{String(val)}</span>;
    if (typeof val === "number")
      return <span style={{ color: "#f59e0b" }}>{val}</span>;
    if (typeof val === "string")
      return <span style={{ color: "#10b981" }}>"{val}"</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span>{"[]"}</span>;
      return (
        <span>
          {"["}
          <div style={{ paddingRight: (depth + 1) * 16 }}>
            {val.map((item, i) => (
              <div key={i}>
                {renderValue(item, depth + 1)}
                {i < val.length - 1 ? "," : ""}
              </div>
            ))}
          </div>
          {"]"}
        </span>
      );
    }
    if (typeof val === "object") {
      const entries = Object.entries(val);
      if (entries.length === 0) return <span>{"{}"}</span>;
      return (
        <span>
          {"{"}
          <div style={{ paddingRight: (depth + 1) * 16 }}>
            {entries.map(([k, v], i) => (
              <div key={k}>
                <span style={{ color: "#60a5fa" }}>"{k}"</span>
                <span style={{ color: "#94a3b8" }}>: </span>
                {renderValue(v, depth + 1)}
                {i < entries.length - 1 ? "," : ""}
              </div>
            ))}
          </div>
          {"}"}
        </span>
      );
    }
    return <span>{String(val)}</span>;
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#1e293b",
          borderRadius: 12,
          padding: 24,
          maxWidth: "80vw",
          maxHeight: "80vh",
          overflow: "auto",
          minWidth: 400,
          boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
          direction: "ltr",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 style={{ color: "#f1f5f9", margin: 0, fontSize: 16 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#94a3b8",
              fontSize: 20,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <pre
          style={{
            margin: 0,
            fontFamily: "monospace",
            fontSize: 13,
            lineHeight: 1.6,
            color: "#e2e8f0",
            background: "transparent",
          }}
        >
          {renderValue(data)}
        </pre>
      </div>
    </div>
  );
};

const Logs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [method, setMethod] = useState(searchParams.get("method") || "all");
  const [userId, setUserId] = useState(searchParams.get("userId") || "");
  const [ipAddress, setIpAddress] = useState(
    searchParams.get("ipAddress") || "",
  );
  // "" = all, "4xx" = client errors, "5xx" = server errors, or exact code like "404"
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("statusFilter") || "",
  );
  const [startDate, setStartDate] = useState(
    searchParams.get("startDate") || moment().format("jYYYY/jMM/jDD"),
  );
  const [endDate, setEndDate] = useState(
    searchParams.get("endDate") || moment().format("jYYYY/jMM/jDD"),
  );
  const [showAdmin, setShowAdmin] = useState(
    searchParams.get("showAdmin") === "true",
  );

  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const [modal, setModal] = useState<{ title: string; data: any } | null>(null);
  const [stats, setStats] = useState<any>(null);

  const currentPage = Number(searchParams.get("page")) || 1;

  const methodTitles: Record<string, string> = {
    all: "همه",
    GET: "GET",
    POST: "POST",
    PUT: "PUT",
    DELETE: "DELETE",
    PATCH: "PATCH",
  };

  const errorTabs = [
    { key: "", label: "همه" },
    { key: "4xx", label: "خطای ۴xx" },
    { key: "5xx", label: "خطای ۵xx" },
  ];

  const openModal = (title: string, bodyData: any) =>
    setModal({ title, data: bodyData });

  const buildApiParams = (page: number) => {
    const params: any = { page, limit: itemsPerPage };
    if (query) params.pathname = query;
    if (method && method !== "all") params.method = method;
    if (userId) params.userId = userId;
    if (ipAddress) params.ipAddress = ipAddress;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (showAdmin) params.showAdmin = true;

    if (statusFilter === "4xx") {
      params.minStatusCode = 400;
      params.maxStatusCode = 499;
    } else if (statusFilter === "5xx") {
      params.minStatusCode = 500;
      params.maxStatusCode = 599;
    } else if (statusFilter) {
      params.statusCode = statusFilter;
    }

    return params;
  };

  const fetchLogs = async (page = currentPage) => {
    dispatch(setLoading(true));
    try {
      const res = await restApi(endpoints.logs.search, true).get(
        buildApiParams(page),
      );
      setLogs(res.data || []);
      setTotal(res.total || 0);
      setPageCount(
        res.totalPages || Math.ceil((res.total || 0) / itemsPerPage) || 1,
      );
    } catch (error) {
      console.error("Error fetching logs:", error);
      Swal.fire({
        title: "خطا",
        text: "خطا در دریافت لاگ‌ها",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchStats = async () => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (showAdmin) params.showAdmin = true;
    try {
      const res = await restApi(endpoints.logs.stats, true).get(params);
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Re-fetch whenever page, itemsPerPage, method, or statusFilter changes
  useEffect(() => {
    fetchLogs(currentPage);
  }, [currentPage, itemsPerPage, method, statusFilter]);

  // Fetch stats once on mount; date changes only take effect when the user
  // presses the search button (handled in handleSearch).
  useEffect(() => {
    fetchStats();
  }, []);

  const pushParams = (overrides: Record<string, string> = {}) => {
    const base: Record<string, string> = {
      page: "1",
      ...(method !== "all" && { method }),
      ...(query && { query }),
      ...(userId && { userId }),
      ...(ipAddress && { ipAddress }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(statusFilter && { statusFilter }),
      ...(showAdmin && { showAdmin: "true" }),
    };
    setSearchParams({ ...base, ...overrides });
  };

  const handleSearch = () => {
    pushParams();
    fetchLogs(1);
    fetchStats();
  };

  const clearFilters = () => {
    setQuery("");
    setMethod("all");
    setUserId("");
    setIpAddress("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setShowAdmin(false);
    setSearchParams({ page: "1" });
  };

  const handlePageChange = (event: { selected: number }) => {
    const newPage = event.selected + 1;
    pushParams({ page: newPage.toString() });
  };

  const handleMethodChange = (key: string) => {
    setMethod(key);
    pushParams({ method: key, page: "1" });
  };

  const handleStatusFilterChange = (key: string) => {
    setStatusFilter(key);
    pushParams({ statusFilter: key, page: "1" });
  };

  const bodyPreview = (body: any) => {
    const str = JSON.stringify(body);
    return str.length > 60 ? str.substring(0, 60) + "..." : str;
  };

  const list = () =>
    logs.map((log: any, index: number) => {
      const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;
      const code = log.statusCode;
      const statusClass =
        code >= 500
          ? "status5xx"
          : code >= 400
            ? "status4xx"
            : code >= 200
              ? "status2xx"
              : "";

      return (
        <tr className="dashTr2" key={"log" + index}>
          <td>
            {log.requestBody ? (
              <code
                style={{
                  fontSize: "11px",
                  wordBreak: "break-all",
                  cursor: "pointer",
                  color: "#60a5fa",
                }}
                onClick={() => openModal("بدنه درخواست", log.requestBody)}
                title="کلیک برای مشاهده کامل"
              >
                {bodyPreview(log.requestBody)}
              </code>
            ) : (
              "-"
            )}
          </td>
          <td>
            {log.responseBody ? (
              <code
                style={{
                  fontSize: "11px",
                  wordBreak: "break-all",
                  cursor: "pointer",
                  color: "#60a5fa",
                }}
                onClick={() => openModal("بدنه پاسخ", log.responseBody)}
                title="کلیک برای مشاهده کامل"
              >
                {bodyPreview(log.responseBody)}
              </code>
            ) : (
              "-"
            )}
          </td>
          <td>
            {code ? (
              <span className={`methodBadge ${statusClass}`}>{code}</span>
            ) : (
              "-"
            )}
          </td>
          <td>
            {log.userAgent ? log.userAgent.substring(0, 30) + "..." : "-"}
          </td>
          <td>{log.ipAddress}</td>
          <td>{log.userId || "-"}</td>
          <td>
            <span className={`methodBadge method${log.method}`}>
              {log.method}
            </span>
          </td>
          <td style={{ maxWidth: "200px", wordBreak: "break-all" }}>
            {log.pathname}
          </td>
          <td>
            <div className="column">
              <p>{log.date}</p>
              <p>{log.time}</p>
            </div>
          </td>
          <td>{rowNumber}</td>
        </tr>
      );
    });

  const paginateProps = {
    breakLabel: "...",
    nextLabel: "بعدی >",
    onPageChange: handlePageChange,
    forcePage: Math.max(0, currentPage - 1),
    pageRangeDisplayed: isMobile ? 3 : 5,
    pageCount,
    previousLabel: "< قبلی",
    renderOnZeroPageCount: null as any,
    className: "pagination",
    pageClassName: "paginationBreak",
    previousClassName: "paginationBreak",
    nextClassName: "paginationBreak",
    activeClassName: "paginationActive",
    disabledClassName: "paginationDisabled",
  };

  const statusCodeCount = (range: string) => {
    if (!stats?.statusCodes) return 0;
    return Object.entries(stats.statusCodes)
      .filter(([c]) =>
        range === "4xx"
          ? Number(c) >= 400 && Number(c) < 500
          : Number(c) >= 500,
      )
      .reduce((sum, [, v]) => sum + (v as number), 0);
  };

  return (
    <>
      {modal && (
        <JsonModal
          title={modal.title}
          data={modal.data}
          onClose={() => setModal(null)}
        />
      )}

      <div className="dashboardBody">
        <Sidebar />

        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">لاگ‌های سیستم</h1>

          {stats && (
            <div className="statsContainer">
              <div className="statCard">
                <h3>کل درخواست‌ها</h3>
                <p className="statNumber">
                  {stats.totalRequests?.toLocaleString("fa-IR")}
                </p>
              </div>
              <div className="statCard">
                <h3>کاربران یکتا</h3>
                <p className="statNumber">
                  {stats.uniqueUsers?.toLocaleString("fa-IR")}
                </p>
              </div>
              <div className="statCard">
                <h3>IP های یکتا</h3>
                <p className="statNumber">
                  {stats.uniqueIPs?.toLocaleString("fa-IR")}
                </p>
              </div>
              {stats.statusCodes &&
                Object.entries(stats.statusCodes)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([code, count]: [string, any]) => {
                    const cls =
                      Number(code) >= 500
                        ? "status5xx"
                        : Number(code) >= 400
                          ? "status4xx"
                          : "status2xx";
                    return (
                      <div
                        className="statCard clickable"
                        key={code}
                        onClick={() => handleStatusFilterChange(code)}
                        style={{ cursor: "pointer" }}
                        title="کلیک برای فیلتر"
                      >
                        <h3>
                          <span className={`methodBadge ${cls}`}>{code}</span>
                        </h3>
                        <p className="statNumber">
                          {count?.toLocaleString("fa-IR")}
                        </p>
                      </div>
                    );
                  })}
              {stats.methods &&
                Object.entries(stats.methods).map(
                  ([m, count]: [string, any]) => (
                    <div className="statCard" key={m}>
                      <h3>{m}</h3>
                      <p className="statNumber">
                        {count?.toLocaleString("fa-IR")}
                      </p>
                    </div>
                  ),
                )}
            </div>
          )}

          {/* Method Tabs */}
          <div className="dashTabs">
            {Object.entries(methodTitles).map(([key, value]) => (
              <span
                key={key}
                className={`ordersTag clickable ${key === method ? "activeTab" : ""}`}
                onClick={() => handleMethodChange(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleMethodChange(key)}
              >
                {value}
                {stats?.methods && key !== "all" && (
                  <span
                    className={`numberTag ${key === method ? "activeTab" : ""}`}
                  >
                    {stats.methods[key]?.toLocaleString("fa-IR") || 0}
                  </span>
                )}
                {key === "all" && stats && (
                  <span
                    className={`numberTag ${key === method ? "activeTab" : ""}`}
                  >
                    {stats.totalRequests?.toLocaleString("fa-IR") || 0}
                  </span>
                )}
              </span>
            ))}
          </div>

          {/* Error Filter Tabs */}
          <div className="dashTabs" style={{ marginTop: 8 }}>
            {errorTabs.map(({ key, label }) => (
              <span
                key={key}
                className={`ordersTag clickable ${statusFilter === key ? "activeTab" : ""}`}
                onClick={() => handleStatusFilterChange(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleStatusFilterChange(key)
                }
              >
                {label}
                {(key === "4xx" || key === "5xx") && stats?.statusCodes && (
                  <span
                    className={`numberTag ${statusFilter === key ? "activeTab" : ""}`}
                  >
                    {statusCodeCount(key).toLocaleString("fa-IR")}
                  </span>
                )}
              </span>
            ))}
          </div>

          {/* Filters */}
          <div className="filtersSection">
            <div className="filterRow">
              <div className="dashboardseaechBox">
                <i className="dashMagnifierIcon"></i>
                <input
                  className="dashSearchInput"
                  placeholder="جستجو در مسیر (pathname)"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  aria-label="جستجوی مسیر"
                />
              </div>
              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="شناسه کاربر"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  aria-label="شناسه کاربر"
                />
              </div>
              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="آدرس IP"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  aria-label="آدرس IP"
                />
              </div>
              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="کد وضعیت دقیق (مثال: 500)"
                  value={!statusFilter.includes("x") ? statusFilter : ""}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  aria-label="کد وضعیت"
                />
              </div>
            </div>
            <div className="filterRow">
              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="از تاریخ (مثال: 1403/09/01)"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="از تاریخ"
                />
              </div>
              <div className="dashboardseaechBox">
                <input
                  className="dashSearchInput"
                  placeholder="تا تاریخ (مثال: 1403/09/30)"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="تا تاریخ"
                />
              </div>
              <div className="switch-container">
                <Switch
                  checked={showAdmin}
                  onChange={(checked: boolean) => setShowAdmin(checked)}
                />
                <span className="switch-label">نمایش ادمین</span>
              </div>
              <button className="searchButton" onClick={handleSearch}>
                <i className="dashMagnifierIcon"></i>
                جستجو
              </button>
              <button className="clearButton" onClick={clearFilters}>
                پاک کردن فیلترها
              </button>
            </div>
          </div>

          <div className="searchContainer">
            <span className="backSpan keepRight">
              <span className="dashboardHeader">
                <p>تعداد کل: {total.toLocaleString("fa-IR")}</p>
              </span>
            </span>
            <div className="itemsPerPage">
              <label htmlFor="itemPerPage">تعداد در صفحه</label>
              <input
                id="itemPerPage"
                type="number"
                min="1"
                max="100"
                defaultValue={itemsPerPage}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value > 0 && value <= 100) setItemsPerPage(value);
                }}
                onBlur={(e) => {
                  if (!e.target.value || Number(e.target.value) <= 0) {
                    e.target.value = itemsPerPage.toString();
                  }
                }}
              />
            </div>
          </div>

          <ReactPaginate {...paginateProps} />

          <div className="table-container">
            <table>
              <thead>
                <tr className="dashTr1 blueText">
                  <th>بدنه درخواست</th>
                  <th>بدنه پاسخ</th>
                  <th>کد وضعیت</th>
                  <th>User Agent</th>
                  <th>IP</th>
                  <th>شناسه کاربر</th>
                  <th>متد</th>
                  <th>مسیر</th>
                  <th>تاریخ و زمان</th>
                  <th>ردیف</th>
                </tr>
              </thead>
              <tbody>{list()}</tbody>
            </table>
          </div>

          <ReactPaginate {...paginateProps} />
        </main>
      </div>
    </>
  );
};

export default Logs;
